import React, { FunctionComponent } from "react";
import { Trans } from "@lingui/macro";
import { PersonalTaxFormTitle } from "../PersonalTaxFormTitle";
import { useFormikContext, useField } from "formik";
import { useCity } from "./citiesApi";
import { Disclaimer } from "./Disclaimer";
import { useHelpDialogContext } from "components/header/help/HelpDialogProvider";
import { NumberField } from "components/fields/NumberField";
import NumberFormat from "react-number-format";
import { getTaxCodesForGewest, TaxCodes } from "../aangiftecodesForm/taxCodesApi";
import { isRubriekKey } from "../taxCalculationResult/taxCalculationApi";
import { usePrevious } from "hooks/usePrevious";
import { InputField } from "components/fields/InputField";
import { pipe } from "fp-ts/lib/function";
import { array, either, io } from "fp-ts";
import { identity } from "ramda";

export interface Props {}

export const PersonaliaForm: FunctionComponent<Props> = () => {
  const { openDialog } = useHelpDialogContext();
  const [{ value: postalCode }] = useField("postalcode");
  const [{ value: gewest }] = useField("gewest");
  const previousGewest = usePrevious(gewest);
  const city = useCity(postalCode);
  const { setFieldValue, values } = useFormikContext<{ trate: number }>();

  React.useEffect(() => {
    if (postalCode && postalCode.length === 4) {
      if (!city) {
        setFieldValue("trate", "");
        setFieldValue("town", "");
        setFieldValue("gewest", "");
        return;
      }
      setFieldValue("trate", city.taxRate);
      setFieldValue("town", city.name);
      setFieldValue("gewest", city.gewest);
    }
  }, [postalCode, city, setFieldValue]);

  React.useEffect(() => {
    const clearCodesNotInNewGewest = (previousGewestCodes: TaxCodes) => (nextGewestCodes: TaxCodes) => {
      const clearField = (code: string): io.IO<void> => () => setFieldValue(`c${code}`, undefined);

      return pipe(
        Object.keys(values),
        array.filter(isRubriekKey),
        array.map((key) => key.substr(1)),
        array.filter((code) => !nextGewestCodes[code] && previousGewestCodes[code]),
        io.traverseArray(clearField)
      );
    };

    if (gewest && previousGewest && gewest !== previousGewest) {
      pipe(
        either.of(clearCodesNotInNewGewest),
        either.ap(getTaxCodesForGewest(previousGewest)),
        either.ap(getTaxCodesForGewest(gewest)),
        either.fold(() => io.Do, identity)
      )();
    }
  }, [gewest, values, previousGewest, setFieldValue]);

  return (
    <div className="main">
      <div id="tab_information">
        <PersonalTaxFormTitle icon="info">
          <Trans>Personalia</Trans>
        </PersonalTaxFormTitle>
        <div className="velden">
          <div className="veld lang">
            <label htmlFor="postalcode">
              <Trans>Postcode</Trans>
            </label>
            <InputField type="text" id="postalcode" name="postalcode" maxLength={4} />
          </div>

          <div className="veld lang">
            <label htmlFor="town">
              <Trans>Gemeente</Trans>
            </label>
            <InputField type="text" name="town" id="town" disabled />
          </div>

          <div className="veld kort">
            <label htmlFor="trate">
              <Trans>Gemeentebelasting</Trans>
            </label>
            <NumberFormat
              id="trate"
              name="trate"
              decimalScale={2}
              fixedDecimalScale
              disabled
              value={values.trate ? values.trate / 100 : ""}
            />
            &nbsp;%
          </div>
        </div>
        <div className="velden">
          <div className="titel">
            <h2>
              <Trans>Gemiddelde aanslagvoet vorig jaar</Trans>&nbsp;
              <button
                className="avehelp"
                type="button"
                onClick={() => openDialog({ defaultSelectedCategoryIndex: 1, defaultSelectedItemInCategoryIndex: 4 })}
              >
                [?]
              </button>
            </h2>
          </div>

          <div className="veld kort">
            <label htmlFor="c3006">
              <Trans>De aangever</Trans>
            </label>
            <NumberField formatAsString name="c3006" className="percentage" />
            &nbsp;%
          </div>

          <div className="veld kort">
            <label htmlFor="c3007">
              <Trans>De partner</Trans>
            </label>
            <NumberField formatAsString name="c3007" className="percentage" />
            &nbsp;%
          </div>
        </div>
        <Disclaimer />
      </div>
    </div>
  );
};

PersonaliaForm.displayName = "PersonaliaForm";
